import { useState, useEffect, useRef } from "react";
import { Camera, Check, Loader, PenLine, Trash2 } from "lucide-react";
import { supabase } from "./supabase";
import { KO, sendPushToAll } from "./helpers";

export function LunchOrder({lang, isAdmin, empName, refreshKey}) {
  const KO = lang === "kr";
  const SP = lang === "es";
  const DAYS = [
    {key:"mon", kr:"월요일", en:"Monday", es:"Lunes"},
    {key:"tue", kr:"화요일", en:"Tuesday", es:"Martes"},
    {key:"wed", kr:"수요일", en:"Wednesday", es:"Miércoles"},
    {key:"thu", kr:"목요일", en:"Thursday", es:"Jueves"},
    {key:"fri", kr:"금요일", en:"Friday", es:"Viernes"},
  ];

  const [menus, setMenus] = useState({});
  const [orders, setOrders] = useState([]);
  const [myOrders, setMyOrders] = useState({});
  const [weekStart, setWeekStart] = useState(getNextMondayFromNow());
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("order");
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  function fmtLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,"0");
    const day = String(d.getDate()).padStart(2,"0");
    return `${y}-${m}-${day}`;
  }

  function getNextMonday() {
    const d = new Date(weekStart+"T00:00:00");
    d.setDate(d.getDate() + 7);
    return fmtLocal(d);
  }

  function getNextMondayFromNow() {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 1 : 8 - day;
    d.setDate(d.getDate() + diff);
    return fmtLocal(d);
  }

  function getPrevMonday() {
    const d = new Date(weekStart+"T00:00:00");
    d.setDate(d.getDate() - 7);
    return fmtLocal(d);
  }

  function fmtWeek(ws) {
    const d = new Date(ws+"T00:00:00");
    const end = new Date(ws+"T00:00:00");
    end.setDate(end.getDate() + 4);
    return `${d.getMonth()+1}/${d.getDate()} ~ ${end.getMonth()+1}/${end.getDate()}`;
  }

  useEffect(() => { fetchData(); }, [weekStart, refreshKey]);

  // 열어 둔 화면도 새 주문/메뉴가 보이도록: 30초마다 + 앱이 다시 앞으로 나올 때 새로 읽기 (편집 중에는 건너뜀)
  const pollRef = useRef({isEditing:false, uploading:false});
  pollRef.current = {isEditing, uploading};
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      if (pollRef.current.isEditing || pollRef.current.uploading) return;
      fetchData();
    };
    const iv = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", refresh); };
  }, [weekStart]);

  async function fetchData() {
    setLoading(true);
    const { data: menuData } = await supabase
      .from("lunch_menus")
      .select("*")
      .eq("week_start", weekStart);

    const menuMap = {};
    (menuData || []).forEach(m => { menuMap[m.day_of_week] = m; });
    setMenus(menuMap);

    const { data: orderData } = await supabase
      .from("lunch_orders")
      .select("*")
      .eq("week_start", weekStart);
    setOrders(orderData || []);

    const myMap = {};
    (orderData || []).filter(o => o.emp_name === empName).forEach(o => {
      myMap[o.day_of_week] = o.item_kr;
    });
    setMyOrders(myMap);
    const { data: empData } = await supabase.from("employees").select("name,status").eq("status","Active");
    setEmployees(empData || []);
    setLoading(false);
    setInitialLoaded(true);
  }

  const [manualInput, setManualInput] = useState({});
  const [ocrLoading, setOcrLoading] = useState(false);
  const fileInputRef = useRef(null);

  // 이미지 → base64 변환
  function imageToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // 사진 OCR → 메뉴 자동입력
  async function handleOCR(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setOcrLoading(true);
    try {
      const imageBase64 = await imageToBase64(file);
      const res = await fetch("https://spjouqdizmcpehsspfed.supabase.co/functions/v1/ocr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64 }),
      });
      const data = await res.json();
      if (data.error === "NO_HEADER") {
        alert(KO
          ? "메뉴표에서 요일 헤더(월/화/수/목/금)를 찾지 못했습니다.\n표 형식의 메뉴 사진인지 확인해주세요."
          : SP ? "No se encontraron los encabezados de días (Lun~Vie) en la foto.\nUse una foto de menú en formato de tabla."
          : "Could not find day headers (Mon~Fri) in the photo.\nPlease use a table-format menu image.");
        setOcrLoading(false);
        return;
      }
      if (data.error) throw new Error(data.error);
      // columns: [mon[], tue[], wed[], thu[], fri[]]
      const cols = data.columns;
      if (!cols || cols.every(c => c.length === 0)) {
        alert(KO
          ? "메뉴 내용을 인식하지 못했습니다. 더 선명한 사진으로 다시 시도해주세요."
          : SP ? "No se pudo leer el contenido del menú. Intente con una foto más nítida."
          : "Could not read menu content. Please try with a clearer photo.");
        setOcrLoading(false);
        return;
      }
      const keys = ["mon", "tue", "wed", "thu", "fri"];
      const next = {};
      keys.forEach((k, i) => { next[k] = (cols[i] || []).join("\n"); });
      setManualInput(next);
    } catch(err) {
      alert(KO ? "사진 인식에 실패했습니다. 수동으로 입력해주세요." : SP ? "Error al reconocer la foto. Ingrese el menú manualmente." : "Photo recognition failed. Please enter manually.");
      console.error(err);
    }
    setOcrLoading(false);
  }

  // 주문 변경 푸시는 서버(DB 트리거 + lunch-order-notify)가 묶어서 발송
  function queueOrderNotification(){}

  function startEdit(){
    const init = {};
    DAYS.forEach(day=>{
      const m = menus[day.key];
      init[day.key] = m ? (m.items_kr||[]).join("\n") : "";
    });
    setManualInput(init);
    setIsEditing(true);
  }

  // DeepL 번역 함수
  async function translateItems(items, target) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch("https://spjouqdizmcpehsspfed.supabase.co/functions/v1/translate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ texts: items, target }),
      });
      const data = await res.json();
      // null entries = translation unavailable for that item
      return Array.isArray(data.translated) ? data.translated : items.map(()=>null);
    } catch(e) {
      console.log("Translation failed:", e);
      return items.map(()=>null);
    }
  }

  // 메뉴 저장
  async function saveManualMenu() {
    setUploading(true);
    try {
      for (const day of DAYS) {
        const raw = (manualInput[day.key] || "").trim();
        if (!raw) continue;
        const items_kr = raw.split("\n").map(s=>s.replace(/[​-‍﻿]/g,"").trim()).filter(Boolean);
        const enRaw = await translateItems(items_kr, "EN-US");
        const esRaw = await translateItems(items_kr, "ES");
        // fallbacks: en -> Korean, es -> en
        const items_en = items_kr.map((k,i)=>enRaw[i] || k);
        const items_es = items_kr.map((k,i)=>esRaw[i] || items_en[i]);
        await supabase.from("lunch_menus").upsert({
          week_start: weekStart,
          day_of_week: day.key,
          items_kr,
          items_en,
          items_es,
        }, { onConflict: "week_start,day_of_week" });
      }
      const weekLabel = fmtWeek(weekStart);
      await sendPushToAll(
        supabase,
        "점심 메뉴 등록",
        `${weekLabel} 점심 메뉴가 등록되었습니다. 주문해주세요.`,
        "Lunch Menu Posted",
        `Lunch menu for ${weekLabel} has been posted. Please place your order.`,
        "/lunch",
        { title: "Menú de almuerzo publicado", body: `El menú de almuerzo de ${weekLabel} ya está publicado. Por favor, haga su pedido.` }
      );
      await fetchData();
      setIsEditing(false);
      setTab("order");
    } catch(err) {
      alert(KO ? "저장에 실패했습니다." : SP ? "Error al guardar el menú." : "Failed to save menu.");
    }
    setUploading(false);
  }

  // 요일 메뉴 삭제 (해당 요일의 주문 기록도 같이 삭제)
  async function deleteDayMenu(dayKey, dayLabel) {
    const confirmMsg = KO
      ? `${dayLabel} 메뉴를 삭제하시겠습니까? 해당 요일에 등록된 주문도 함께 삭제됩니다.`
      : SP ? `¿Eliminar el menú de ${dayLabel}? Los pedidos de ese día también se eliminarán.`
      : `Delete the menu for ${dayLabel}? Orders placed for that day will also be removed.`;
    if (!window.confirm(confirmMsg)) return;
    setUploading(true);
    try {
      await supabase.from("lunch_orders").delete()
        .eq("week_start", weekStart)
        .eq("day_of_week", dayKey);
      await supabase.from("lunch_menus").delete()
        .eq("week_start", weekStart)
        .eq("day_of_week", dayKey);
      await fetchData();
    } catch(err) {
      alert(KO ? "삭제에 실패했습니다." : SP ? "Error al eliminar el menú." : "Failed to delete menu.");
    }
    setUploading(false);
  }

  // 주문 선택
  async function selectItem(dayKey, itemKr, itemEn) {
    const existing = myOrders[dayKey];
    if (existing === itemKr) {
      await supabase.from("lunch_orders").delete()
        .eq("week_start", weekStart)
        .eq("day_of_week", dayKey)
        .eq("emp_name", empName);
      setMyOrders(p => { const n = {...p}; delete n[dayKey]; return n; });
      queueOrderNotification(dayKey, "removed");
    } else {
      const isEdit = !!existing;
      await supabase.from("lunch_orders").upsert({
        week_start: weekStart,
        day_of_week: dayKey,
        emp_name: empName,
        item_kr: itemKr,
        item_en: itemEn,
      }, { onConflict: "week_start,day_of_week,emp_name" });
      setMyOrders(p => ({...p, [dayKey]: itemKr}));
      queueOrderNotification(dayKey, isEdit ? "edited" : "added");
    }
    fetchData();
  }

  // 언어별 메뉴 항목 선택 (스페인어 번역이 없으면 영어 → 한국어 순서로 대체)
  function pickItems(m) {
    if (KO) return m.items_kr || [];
    if (SP) return (m.items_es?.length ? m.items_es : m.items_en?.length ? m.items_en : m.items_kr) || [];
    return (m.items_en?.length ? m.items_en : m.items_kr) || [];
  }
  function esItemLabel(o) {
    const m = menus[o.day_of_week];
    const i = m?.items_kr ? m.items_kr.indexOf(o.item_kr) : -1;
    return (i >= 0 && m.items_es?.[i]) || o.item_en || o.item_kr;
  }

  // 종합 텍스트 생성
  // includeNames: true → 화면 표시용(이름 포함), false → 복사용(이름 제외, 인원수만)
  function getSummaryText(includeNames = true) {
    let text = KO
      ? `[도시락 주문 ${fmtWeek(weekStart)}]\n\n`
      : SP ? `[Pedido de almuerzo ${fmtWeek(weekStart)}]\n\n`
      : `[Lunch Order ${fmtWeek(weekStart)}]\n\n`;

    for (const day of DAYS) {
      const dayOrders = orders.filter(o => o.day_of_week === day.key);
      if (dayOrders.length === 0) continue;
      text += KO ? `📅 ${day.kr}\n` : SP ? `📅 ${day.es}\n` : `📅 ${day.en}\n`;

      const grouped = {};
      dayOrders.forEach(o => {
        const key = KO ? o.item_kr : SP ? esItemLabel(o) : o.item_en;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(o.emp_name);
      });

      Object.entries(grouped).forEach(([item, names]) => {
        text += includeNames
          ? `  • ${item} × ${names.length} (${names.join(", ")})\n`
          : `  • ${item} × ${names.length}\n`;
      });

      if (includeNames) {
        const orderedNames = dayOrders.map(o => o.emp_name);
        const notOrdered = employees.filter(e => !orderedNames.includes(e.name));
        if (notOrdered.length > 0) {
          text += KO
            ? `  ⚠️ 미주문: ${notOrdered.map(e=>e.name).join(", ")}\n`
            : SP ? `  ⚠️ Sin pedido: ${notOrdered.map(e=>e.name).join(", ")}\n`
            : `  ⚠️ Not ordered: ${notOrdered.map(e=>e.name).join(", ")}\n`;
        }
      }
      text += "\n";
    }

    const total = orders.length;
    text += KO ? `총 ${total}개` : SP ? `Total: ${total}` : `Total: ${total}`;
    return text;
  }

  function copySummary() {
    navigator.clipboard.writeText(getSummaryText(false));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading && !initialLoaded) return (
    <div style={{padding:24, textAlign:"center", color:"var(--tx3)"}}>
      <Loader size={20} style={{animation:"spin 1s linear infinite"}}/>
    </div>
  );

  return (
    <div style={{padding:"16px 16px 80px"}}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10}}>
        <div style={{display:"flex", alignItems:"center", gap:8, whiteSpace:"nowrap"}}>
          <button onClick={()=>setWeekStart(getPrevMonday())} style={{background:"none",border:"1px solid var(--bd)",borderRadius:6,padding:"4px 8px",cursor:"pointer",color:"var(--tx)",flexShrink:0}}>‹</button>
          <span style={{fontWeight:700, fontSize:15, whiteSpace:"nowrap"}}>{fmtWeek(weekStart)}</span>
          <button onClick={()=>setWeekStart(getNextMonday())} style={{background:"none",border:"1px solid var(--bd)",borderRadius:6,padding:"4px 8px",cursor:"pointer",color:"var(--tx)",flexShrink:0}}>›</button>
        </div>
        {isAdmin && (
          <div style={{display:"flex", gap:6, width:"100%"}}>
            <button onClick={()=>setTab("order")}
              style={{flex:1,fontSize:12,padding:"7px 4px",borderRadius:8,border:"none",cursor:"pointer",whiteSpace:"nowrap",
                background:tab==="order"?"var(--ac)":"var(--sf2)",color:tab==="order"?"#fff":"var(--tx)"}}>
              {KO ? "주문" : SP ? "Pedido" : "Order"}
            </button>
            <button onClick={()=>{setTab("setup"); setIsEditing(false);}}
              style={{flex:1,fontSize:12,padding:"7px 4px",borderRadius:8,border:"none",cursor:"pointer",whiteSpace:"nowrap",
                background:tab==="setup"?"var(--ac)":"var(--sf2)",color:tab==="setup"?"#fff":"var(--tx)"}}>
              {KO ? "메뉴 설정" : SP ? "Configurar" : "Setup"}
            </button>
            <button onClick={()=>setTab("summary")}
              style={{flex:1,fontSize:12,padding:"7px 4px",borderRadius:8,border:"none",cursor:"pointer",whiteSpace:"nowrap",
                background:tab==="summary"?"var(--ac)":"var(--sf2)",color:tab==="summary"?"#fff":"var(--tx)"}}>
              {KO ? "주문 종합" : SP ? "Resumen" : "Summary"}
            </button>
          </div>
        )}
      </div>

      {/* 메뉴 설정 탭 (관리자) */}
      {tab === "setup" && isAdmin && !isEditing && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
            <div style={{fontWeight:700, fontSize:15}}>
              {KO ? "📋 등록된 메뉴" : SP ? "📋 Menú guardado" : "📋 Saved Menu"}
            </div>
            <button onClick={startEdit} className="btn bs bsec" style={{display:"flex",alignItems:"center",gap:4}}>
              <PenLine size={14}/> {KO ? "수정" : SP ? "Editar" : "Edit"}
            </button>
          </div>
          {Object.keys(menus).length === 0
            ? <div style={{textAlign:"center", color:"var(--tx3)", padding:20, fontSize:13}}>
                {KO ? "아직 등록된 메뉴가 없어요. 수정 버튼을 눌러 입력해주세요." : SP ? "Aún no hay menú. Toque Editar para ingresarlo." : "No menu yet. Tap Edit to enter one."}
              </div>
            : DAYS.map(day => {
                const m = menus[day.key];
                return (
                  <div key={day.key} style={{marginBottom:16}}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
                      <div style={{fontWeight:700, fontSize:13, color:"var(--tx2)"}}>
                        {KO ? day.kr : SP ? day.es : day.en}
                      </div>
                      {m && (
                        <button onClick={()=>deleteDayMenu(day.key, KO?day.kr:SP?day.es:day.en)} disabled={uploading}
                          style={{background:"rgba(220,38,38,.08)",color:"var(--re)",border:"1px solid rgba(220,38,38,.2)",
                            borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",
                            display:"flex",alignItems:"center",gap:4}}>
                          <Trash2 size={12}/> {KO?"삭제":SP ? "Eliminar" : "Delete"}
                        </button>
                      )}
                    </div>
                    {m
                      ? <div style={{display:"flex", flexDirection:"column", gap:6}}>
                          {pickItems(m).map((it,i)=>(
                            <div key={i} style={{
                              padding:"10px 14px", borderRadius:8,
                              background:"var(--sf2)", color:"var(--tx)",
                              fontSize:14
                            }}>{it}</div>
                          ))}
                        </div>
                      : <div style={{fontSize:12, color:"var(--tx3)"}}>{KO?"미등록":SP ? "Sin definir" : "Not set"}</div>
                    }
                  </div>
                );
              })
          }
        </div>
      )}

      {tab === "setup" && isAdmin && isEditing && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4}}>
            <div style={{fontWeight:700, fontSize:15}}>
              {KO ? "📋 메뉴 입력" : SP ? "📋 Ingresar menú" : "📋 Enter Menu"}
            </div>
            <button onClick={()=>setIsEditing(false)} className="btn bs bsec">
              {KO ? "취소" : SP ? "Cancelar" : "Cancel"}
            </button>
          </div>
          <div style={{fontSize:12, color:"var(--tx3)", marginBottom:10}}>
            {KO ? "요일별로 메뉴를 한 줄에 하나씩 입력하세요." : SP ? "Ingrese un platillo por línea para cada día." : "Enter one menu item per line for each day."}
          </div>
          <button
            onClick={()=>fileInputRef.current?.click()}
            disabled={ocrLoading}
            style={{width:"100%", fontSize:13, fontWeight:600, padding:"10px 12px", borderRadius:10,
              border:"1px dashed var(--bd)", background:"var(--sf2)", color:"var(--tx)",
              cursor:ocrLoading?"default":"pointer", marginBottom:16,
              display:"flex", alignItems:"center", justifyContent:"center", gap:6, opacity:ocrLoading?0.6:1}}>
            {ocrLoading
              ? <><Loader size={15} style={{animation:"spin 1s linear infinite"}}/> {KO?"인식 중...":SP ? "Leyendo..." : "Reading..."}</>
              : <><Camera size={15}/> {KO?"사진으로 불러오기":SP ? "Importar desde foto" : "Import from Photo"}</>
            }
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleOCR}/>
          {DAYS.map(day => {
            return (
              <div key={day.key} style={{marginBottom:12}}>
                <div style={{fontWeight:700, fontSize:13, marginBottom:4, color:"var(--tx2)"}}>
                  {KO ? day.kr : SP ? day.es : day.en}
                </div>
                <textarea
                  rows={4}
                  placeholder={KO ? "메뉴를 입력하세요" : SP ? "Ingrese los platillos" : "Enter menu items"}
                  value={manualInput[day.key] ?? ""}
                  onChange={e=>setManualInput(p=>({...p,[day.key]:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",
                    background:"var(--sf2)",color:"var(--tx)",fontSize:13,resize:"vertical",
                    fontFamily:"inherit",boxSizing:"border-box"}}
                />
              </div>
            );
          })}
          <button onClick={saveManualMenu} disabled={uploading}
            style={{width:"100%",padding:"12px",borderRadius:8,border:"none",cursor:"pointer",
              background:"var(--ac)",color:"#fff",fontWeight:700,fontSize:14,
              opacity:uploading?0.7:1,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            {uploading
              ? <><Loader size={16} style={{animation:"spin 1s linear infinite"}}/> {KO?"저장 중...":SP ? "Guardando..." : "Saving..."}</>
              : (KO?"저장":SP ? "Guardar" : "Save")
            }
          </button>
        </div>
      )}

      {/* 주문 종합 탭 (관리자) */}
      {tab === "summary" && isAdmin && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            <div style={{fontWeight:700, fontSize:15}}>
              {KO ? "📋 주문 종합" : SP ? "📋 Resumen de pedidos" : "📋 Order Summary"}
            </div>
            <button onClick={copySummary}
              style={{fontSize:12, padding:"6px 14px", borderRadius:8, border:"none",
                cursor:"pointer", background: copied ? "#34d399" : "var(--ac)", color:"#fff", fontWeight:600}}>
              {copied ? (KO?"복사됨✓":SP ? "¡Copiado!✓" : "Copied✓") : (KO?"복사":SP ? "Copiar" : "Copy")}
            </button>
          </div>
          <pre style={{fontSize:13, color:"var(--tx)", whiteSpace:"pre-wrap", lineHeight:1.7,
            background:"var(--sf2)", padding:12, borderRadius:8, fontFamily:"inherit"}}>
            {orders.length === 0
              ? (KO ? "아직 주문이 없습니다." : SP ? "Aún no hay pedidos." : "No orders yet.")
              : getSummaryText(true)
            }
          </pre>
          {employees.length > 0 && (() => {
            const allOrdered = orders.map(o => o.emp_name);
            const notOrdered = employees.filter(e => !allOrdered.includes(e.name));
            const unique = [...new Set(notOrdered.map(e=>e.name))];
            if (unique.length === 0) return null;
            return (
              <div style={{marginTop:10, padding:"10px 12px", background:"rgba(251,191,36,.1)",
                border:"1px solid rgba(251,191,36,.3)", borderRadius:8, fontSize:13}}>
                <span style={{fontWeight:700, color:"#f59e0b"}}>⚠️ {KO?"미주문 직원":SP ? "Sin pedido" : "Not ordered"}: </span>
                {unique.join(", ")}
              </div>
            );
          })()}
        </div>
      )}

      {/* 주문 탭 (기본) */}
      {tab === "order" && (
        Object.keys(menus).length === 0
          ? <div className="card" style={{textAlign:"center", color:"var(--tx3)", padding:32}}>
              {KO ? "이번 주 메뉴가 아직 등록되지 않았어요." : SP ? "El menú de esta semana aún no está registrado." : "This week's menu hasn't been set up yet."}
              {isAdmin && <div style={{marginTop:8, fontSize:12}}>{KO ? "메뉴 설정에서 사진을 업로드해주세요." : SP ? "Suba una foto en Configurar menú." : "Please upload a photo in Menu Setup."}</div>}
            </div>
          : DAYS.map(day => {
              const m = menus[day.key];
              if (!m) return null;
              const items = pickItems(m);
              const itemsKr = m.items_kr;
              const selected = myOrders[day.key];
              const dayOrderCount = orders.filter(o => o.day_of_week === day.key).length;

              return (
                <div key={day.key} className="card" style={{marginBottom:12}}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
                    <div style={{fontWeight:700, fontSize:15}}>{KO ? day.kr : SP ? day.es : day.en}</div>
                    {isAdmin && <div style={{fontSize:12, color:"var(--tx3)"}}>{dayOrderCount}{KO?"명 주문":SP ? "pedidos" : "ordered"}</div>}
                  </div>
                  <div style={{display:"flex", flexDirection:"column", gap:6}}>
                    {items.map((item, i) => {
                      const isSelected = selected === itemsKr[i];
                      return (
                        <button key={i} onClick={()=>selectItem(day.key, itemsKr[i], (m.items_en||[])[i]||itemsKr[i])}
                          style={{
                            padding:"10px 14px", borderRadius:8, border:"none", cursor:"pointer",
                            textAlign:"left", fontSize:14, fontWeight: isSelected ? 700 : 400,
                            background: isSelected ? "var(--ac)" : "var(--sf2)",
                            color: isSelected ? "#fff" : "var(--tx)",
                            display:"flex", alignItems:"center", justifyContent:"space-between"
                          }}>
                          <span>{item}</span>
                          {isSelected && <Check size={16}/>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
      )}
    </div>
  );
}